import { Image as ImageIcon, X } from "lucide-react";
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { handleStopPropagation } from "./Utils";
import { useDispatch } from "react-redux";
import { showAlert } from "store/reducers/alertReducer";
import { ErrorMessage } from "formik";

const SingleFileUpload = ({
  uploadedFile,
  setUploadedFile,
  maxFileSize = 5 * 1024 * 1024, // 5 MB
  acceptedFileTypes = ["image/png", "image/jpeg"],
  requireSquare = false,
  requireRectangle = false,
  onChange = () => {},
  className = "",
  isShowErrorMsg = false,
  name = "",
}) => {
  const dispatch = useDispatch();

  const formatAcceptedTypes = () => {
    const result = {};
    acceptedFileTypes?.forEach((type) => {
      result[type] = [];
    });
    return result;
  };

  const validateDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        URL.revokeObjectURL(img.src);
        if (requireSquare && width !== height) {
          reject("Image must be square.");
        } else if (requireRectangle && width === height) {
          reject("Image must be rectangular.");
        } else {
          resolve();
        }
      };
      img.onerror = () => reject("Failed to load image.");
    });

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const errorMessage = rejectedFiles[0].errors
          .map((e) =>
            e.code === "file-too-large"
              ? `Please upload a file that is less than ${(maxFileSize / 1024).toFixed(0)} KB in size`
              : e.message
          )
          .join(", ");
        dispatch(
          showAlert({
            text: errorMessage,
            type: "error",
          })
        );
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        try {
          await validateDimensions(file);
          setUploadedFile(file); // Store a single file instead of an array
          if (onChange) {
            onChange(file); // Pass the single file to the parent
          }
        } catch (error) {
          dispatch(
            showAlert({
              text: `File rejected: ${file.name} (${error})`,
              type: "error",
            })
          );
        }
      }
    },
    [setUploadedFile, maxFileSize, requireSquare, requireRectangle, onChange]
  );

  const removeFile = () => {
    setUploadedFile(null);
    if (onChange) {
      onChange(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: formatAcceptedTypes(),
    maxSize: maxFileSize,
    multiple: false, // Only allow a single file
  });

  return (
    <div className="mb_20">
      <div
        {...getRootProps()}
        className={`file_upload g_between ${className}`}
        style={{
          background: isDragActive ? "#f0f0f0" : "#ffffff",
        }}
      >
        <input {...getInputProps()} />

        {!uploadedFile ? (
          <div className="d-flex flex-column w-100 align-items-center">
            <ImageIcon width={32} height={32} className="text_gray mb-2" />
            <p className="w-100">
              <span>Upload a file</span> or drag and drop
              <br />
              (Max {maxFileSize / (1024 * 1024)} MB)
            </p>
          </div>
        ) : (
          <div className="w-100">
            <div className="uploaded_images" onClick={handleStopPropagation}>
              <div className="w-100 position-relative single_img">
                <img
                  src={
                    uploadedFile instanceof File
                      ? URL.createObjectURL(uploadedFile)
                      : uploadedFile
                  }
                  alt=""
                  className="uploaded_image d-block mx-auto"
                />
                <X className="trash_icon" onClick={removeFile} />
              </div>
            </div>
          </div>
        )}
      </div>
      {isShowErrorMsg && (
        <ErrorMessage name={name} component="div" className="text-danger" />
      )}
    </div>
  );
};

export default SingleFileUpload;
